/**
 * TanStack Query client (Phase 2 Scaling — frontend caching layer).
 *
 * Why this exists
 * ---------------
 * Most iStudyPro pages currently fire a fresh `axios.get` on every mount.
 * That means navigating between Dashboard ↔ Subjects ↔ Lectures hits the
 * backend 6–10 extra times per session for data that hasn't changed in
 * minutes. At 5k DAU that's tens of thousands of wasted requests.
 *
 * TanStack Query gives us three things for free:
 *   1. Per-key in-memory cache with stale-while-revalidate behaviour
 *   2. Request deduplication (10 components reading the same key → 1 fetch)
 *   3. Automatic background refresh when the user re-focuses the tab
 *
 * Defaults are tuned conservatively for an EdTech product:
 *   - 60s staleTime → data feels live, but rapid re-renders won't refetch
 *   - 10min gcTime  → cache survives short navigations & back/forward swipes
 *   - retry: 1 with exponential backoff → don't hammer the backend on errors
 *   - refetchOnWindowFocus: false in dev, true in prod (keeps DX fast)
 *
 * Migration strategy: opt-in. Existing axios calls keep working. Hot pages
 * (Dashboard widgets, Subjects list, Habit Progress) should migrate first
 * using the `useQuery` hook with a stable key array.
 */

import { QueryClient } from '@tanstack/react-query';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,            // 1 minute — treats data as fresh
      gcTime: 10 * 60_000,          // 10 minutes — cache retention
      retry: 1,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
      refetchOnWindowFocus: process.env.NODE_ENV === 'production',
      refetchOnReconnect: true,
    },
    mutations: {
      retry: 0,                     // never silently retry POST/PUT/DELETE
    },
  },
});
